import * as React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Button,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    SearchBox,
    Select,
    TableColumnDefinition,
    TableColumnSizingOptions,
    Text,
    tokens,
} from '@fluentui/react-components';
import {
    Add20Regular,
    ArrowClockwise20Regular,
    ChevronRight20Regular,
    Flash20Regular,
    Poll24Regular,
    TaskListSquareLtr20Regular,
} from '@fluentui/react-icons';
import IPoll from '../model/IPoll';
import { PollState } from '../services/DataService';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useIsMobile } from '../app/useMediaQuery';
import { formatDate, formatNumber } from '../lib/format';
import { POLL_STATE_OPTIONS, pollsCount } from '../lib/pollLabels';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import StatusBadge from '../components/ui/StatusBadge';
import { PollTypeIcon } from '../components/ui/PollTypeBadge';
import CopyButton from '../components/ui/CopyButton';
import EmptyState from '../components/ui/EmptyState';
import ErrorBar from '../components/ui/ErrorBar';
import { TableSkeleton } from '../components/ui/Skeletons';
import PollRowMenu from '../components/polls/PollRowMenu';
import { usePollActions } from '../components/polls/usePollActions';

type SortColumn = 'Title' | 'State' | 'AuthorId' | 'Slug' | 'Created' | 'ResponseCount';
type SortDirection = 'asc' | 'desc';

const useStyles = makeStyles({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '16px',
        paddingRight: '16px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    search: {
        flexGrow: 1,
        minWidth: '200px',
        maxWidth: '380px',
        '@media (max-width: 767px)': {
            maxWidth: 'none',
            flexBasis: '100%',
        },
    },
    filter: {
        minWidth: '180px',
        '@media (max-width: 767px)': {
            flexGrow: 1,
        },
    },
    count: {
        marginLeft: 'auto',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        '@media (max-width: 767px)': {
            marginLeft: 0,
        },
    },
    tableWrap: {
        overflowX: 'auto',
    },
    grid: {
        minWidth: '860px',
    },
    row: {
        ':hover .row-actions': {
            opacity: 1,
        },
        ':focus-within .row-actions': {
            opacity: 1,
        },
    },
    titleCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        width: '100%',
        minWidth: 0,
    },
    titleLink: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        ':hover': {
            color: tokens.colorBrandForeground1,
            textDecoration: 'underline',
        },
        ':focus-visible': {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            borderRadius: tokens.borderRadiusSmall,
        },
    },
    rowActions: {
        opacity: 0,
        transitionProperty: 'opacity',
        transitionDuration: tokens.durationFaster,
        '@media (hover: none)': {
            opacity: 1,
        },
    },
    muted: {
        color: tokens.colorNeutralForeground2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
    },
    slugCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: '100%',
        minWidth: 0,
    },
    slug: {
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    numeric: {
        display: 'block',
        width: '100%',
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        color: tokens.colorNeutralForeground2,
    },
    // --- mobile cards --------------------------------------------------------
    cards: {
        display: 'flex',
        flexDirection: 'column',
    },
    card: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        columnGap: '12px',
        rowGap: '6px',
        alignItems: 'start',
        padding: '14px 16px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        cursor: 'pointer',
        ':active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
        },
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minWidth: 0,
    },
    cardTitle: {
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase300,
        overflowWrap: 'anywhere',
    },
    cardMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px 12px',
        flexWrap: 'wrap',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    cardAside: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '4px',
    },
    chevron: {
        color: tokens.colorNeutralForeground4,
    },
});

interface IPollsResponse {
    Items?: IPoll[];
    Total?: number;
}

function comparable(poll: IPoll, column: SortColumn): string | number {
    switch (column) {
        case 'Title': return (poll.Title ?? '').toLowerCase();
        case 'State': return poll.State ?? -1;
        case 'AuthorId': return (poll.AuthorName ?? poll.AuthorId ?? '').toLowerCase();
        case 'Slug': return (poll.Slug ?? '').toLowerCase();
        case 'ResponseCount': return poll.ResponseCount ?? 0;
        case 'Created':
        default: {
            const date = poll.Created ? new Date(poll.Created) : undefined;
            return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
        }
    }
}

function matchesSearch(poll: IPoll, query: string): boolean {
    if (!query) return true;
    const q = query.toLowerCase();
    return [poll.Title, poll.Slug, poll.SlugDeck, poll.AuthorName, poll.AuthorId, poll.Description]
        .some((value) => (value ?? '').toLowerCase().includes(q));
}

const PollsPage: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const dataService = useDataService();

    const [searchInput, setSearchInput] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [stateFilter, setStateFilter] = React.useState<string>('');
    const [sortColumn, setSortColumn] = React.useState<SortColumn>('Created');
    const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

    // Debounced search: type freely, query after a short pause.
    React.useEffect(() => {
        const id = window.setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => window.clearTimeout(id);
    }, [searchInput]);

    const list = useAsync(async () => {
        const data = await dataService.getPolls({
            PageIndex: 0,
            PageSize: 50,
            SearchQuery: search,
            SortColumn: sortColumn,
            SortDirection: sortDirection === 'asc' ? 'ascending' : 'descending',
        }) as IPollsResponse | IPoll[] | undefined;
        const items = Array.isArray(data) ? data : (data?.Items ?? []);
        return items;
    }, [search, sortColumn, sortDirection]);

    const actions = usePollActions({ onDeleted: () => list.reload() });

    const items = React.useMemo(() => {
        const source = (list.data ?? [])
            .filter((poll) => matchesSearch(poll, search))
            .filter((poll) => stateFilter === '' || String(poll.State) === stateFilter);
        const direction = sortDirection === 'asc' ? 1 : -1;
        return source.slice().sort((a, b) => {
            const av = comparable(a, sortColumn);
            const bv = comparable(b, sortColumn);
            if (av < bv) return -1 * direction;
            if (av > bv) return direction;
            return 0;
        });
    }, [list.data, search, stateFilter, sortColumn, sortDirection]);

    const onSortChange = (_: unknown, next: { sortColumn: string | number | undefined; sortDirection: 'ascending' | 'descending' }): void => {
        if (!next.sortColumn) return;
        setSortColumn(next.sortColumn as SortColumn);
        setSortDirection(next.sortDirection === 'ascending' ? 'asc' : 'desc');
    };

    const columns: TableColumnDefinition<IPoll>[] = React.useMemo(() => [
        createTableColumn<IPoll>({
            columnId: 'Type',
            renderHeaderCell: () => '',
            renderCell: (poll) => <PollTypeIcon poll={poll} />,
        }),
        createTableColumn<IPoll>({
            columnId: 'Title',
            compare: () => 0,
            renderHeaderCell: () => 'Название',
            renderCell: (poll) => (
                <div className={styles.titleCell}>
                    <RouterLink to={`/polls/${encodeURIComponent(poll.Id ?? '')}`} className={styles.titleLink}>
                        {poll.Title || 'Без названия'}
                    </RouterLink>
                    <PollRowMenu poll={poll} actions={actions} className={mergeClasses('row-actions', styles.rowActions)} />
                </div>
            ),
        }),
        createTableColumn<IPoll>({
            columnId: 'State',
            compare: () => 0,
            renderHeaderCell: () => 'Статус',
            renderCell: (poll) => <StatusBadge state={poll.State} size="small" />,
        }),
        createTableColumn<IPoll>({
            columnId: 'AuthorId',
            compare: () => 0,
            renderHeaderCell: () => 'Автор',
            renderCell: (poll) => <Text className={styles.muted}>{poll.AuthorName ?? poll.AuthorId ?? '—'}</Text>,
        }),
        createTableColumn<IPoll>({
            columnId: 'Slug',
            compare: () => 0,
            renderHeaderCell: () => 'Ссылка',
            renderCell: (poll) => (
                <div className={styles.slugCell}>
                    <Text className={styles.slug} title={poll.Slug}>{poll.Slug ?? '—'}</Text>
                    <CopyButton
                        value={actions.surveyUrl(poll)}
                        label="Копировать ссылку на опрос"
                        successMessage="Ссылка скопирована"
                        className={mergeClasses('row-actions', styles.rowActions)}
                    />
                </div>
            ),
        }),
        createTableColumn<IPoll>({
            columnId: 'Created',
            compare: () => 0,
            renderHeaderCell: () => 'Создан',
            renderCell: (poll) => <Text className={styles.muted}>{formatDate(poll.Created, 'dd.MM.yyyy')}</Text>,
        }),
        createTableColumn<IPoll>({
            columnId: 'ResponseCount',
            compare: () => 0,
            renderHeaderCell: () => <span style={{ display: 'block', width: '100%', textAlign: 'right' }}>Ответов</span>,
            renderCell: (poll) => <Text className={styles.numeric}>{formatNumber(poll.ResponseCount)}</Text>,
        }),
    ], [styles, actions]);

    const columnSizing: TableColumnSizingOptions = {
        Type: { minWidth: 44, idealWidth: 44, defaultWidth: 44 },
        Title: { minWidth: 240, idealWidth: 360 },
        State: { minWidth: 120, idealWidth: 140 },
        AuthorId: { minWidth: 130, idealWidth: 170 },
        Slug: { minWidth: 150, idealWidth: 190 },
        Created: { minWidth: 100, idealWidth: 110 },
        ResponseCount: { minWidth: 90, idealWidth: 100 },
    };

    const newPollMenu = (
        <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
                <Button appearance="primary" icon={<Add20Regular />}>Новый опрос</Button>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItem icon={<Flash20Regular />} secondaryContent="1 вопрос" onClick={() => navigate('/polls/new/quick')}>Быстрый опрос</MenuItem>
                    <MenuItem icon={<TaskListSquareLtr20Regular />} secondaryContent="Несколько" onClick={() => navigate('/polls/new/complex')}>Комплексный опрос</MenuItem>
                </MenuList>
            </MenuPopover>
        </Menu>
    );

    const hasFilters = !!search || stateFilter !== '';
    const showEmpty = !list.loading && !list.error && items.length === 0;

    return (
        <>
            <PageHeader
                title="Опросы"
                subtitle="Опросы обратной связи по мероприятиям: создание, ссылки и QR-коды, результаты."
                actions={[
                    { key: 'refresh', label: 'Обновить', icon: <ArrowClockwise20Regular />, onClick: list.reload, iconOnly: true, disabled: list.loading, appearance: 'secondary' },
                ]}
                extra={newPollMenu}
            />

            <ErrorBar message={list.error} title="Не удалось загрузить опросы" onRetry={list.reload} />

            <Section flush>
                <div className={styles.toolbar} role="search">
                    <SearchBox
                        className={styles.search}
                        placeholder="Название, ссылка или автор"
                        aria-label="Поиск опросов"
                        value={searchInput}
                        onChange={(_, data) => setSearchInput(data.value)}
                    />
                    <Select
                        className={styles.filter}
                        aria-label="Фильтр по статусу"
                        value={stateFilter}
                        onChange={(_, data) => setStateFilter(data.value)}
                    >
                        <option value="">Все статусы</option>
                        {POLL_STATE_OPTIONS.filter((o) => o.value !== PollState.Deleted).map((option) => (
                            <option key={option.value} value={String(option.value)}>{option.label}</option>
                        ))}
                    </Select>
                    <Text className={styles.count}>
                        {list.loading ? 'Загрузка…' : pollsCount(items.length)}
                    </Text>
                </div>

                {list.loading && <TableSkeleton rows={isMobile ? 5 : 7} />}

                {showEmpty && (
                    <div style={{ padding: 16 }}>
                        <EmptyState
                            icon={<Poll24Regular />}
                            title={hasFilters ? 'Ничего не найдено' : 'Опросов пока нет'}
                            description={hasFilters
                                ? 'Попробуйте изменить запрос или сбросить фильтр по статусу.'
                                : 'Создайте первый опрос: быстрый с одним вопросом или комплексный с несколькими.'}
                            actions={hasFilters ? (
                                <Button appearance="secondary" onClick={() => { setSearchInput(''); setStateFilter(''); }}>
                                    Сбросить фильтры
                                </Button>
                            ) : newPollMenu}
                        />
                    </div>
                )}

                {!list.loading && items.length > 0 && !isMobile && (
                    <div className={styles.tableWrap}>
                        <DataGrid
                            className={styles.grid}
                            items={items}
                            columns={columns}
                            getRowId={(poll) => poll.Id ?? ''}
                            sortable
                            sortState={{ sortColumn, sortDirection: sortDirection === 'asc' ? 'ascending' : 'descending' }}
                            onSortChange={onSortChange}
                            resizableColumns
                            columnSizingOptions={columnSizing}
                            focusMode="composite"
                        >
                            <DataGridHeader>
                                <DataGridRow>
                                    {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                                </DataGridRow>
                            </DataGridHeader>
                            <DataGridBody<IPoll>>
                                {({ item, rowId }) => (
                                    <DataGridRow<IPoll> key={rowId} className={styles.row}>
                                        {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                                    </DataGridRow>
                                )}
                            </DataGridBody>
                        </DataGrid>
                    </div>
                )}

                {!list.loading && items.length > 0 && isMobile && (
                    <div className={styles.cards} role="list">
                        {items.map((poll) => {
                            const to = `/polls/${encodeURIComponent(poll.Id ?? '')}`;
                            return (
                                <div
                                    key={poll.Id}
                                    role="listitem"
                                    className={styles.card}
                                    onClick={() => navigate(to)}
                                >
                                    <PollTypeIcon poll={poll} />
                                    <div className={styles.cardBody}>
                                        <RouterLink to={to} className={styles.titleLink} onClick={(e) => e.stopPropagation()}>
                                            <span className={styles.cardTitle}>{poll.Title || 'Без названия'}</span>
                                        </RouterLink>
                                        <div className={styles.cardMeta}>
                                            <StatusBadge state={poll.State} size="small" />
                                            <span>{formatDate(poll.Created, 'dd.MM.yyyy')}</span>
                                            <span>{poll.AuthorName ?? poll.AuthorId ?? ''}</span>
                                        </div>
                                    </div>
                                    <div className={styles.cardAside}>
                                        <PollRowMenu poll={poll} actions={actions} size="medium" />
                                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                                            {formatNumber(poll.ResponseCount)} отв. <ChevronRight20Regular className={styles.chevron} style={{ verticalAlign: 'middle' }} />
                                        </Text>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Section>

            {actions.dialog}
        </>
    );
};

export default PollsPage;
